import { createSelector } from "reselect";

import { Right } from "constants/right";
import Role from "constants/role";
import { alphabeticalSortBySelector } from "helpers/sort";
import { IStoreState } from "interfaces/storeState";
import { IWithAgentsState, selectLoggedInAgent } from "store/entities/agents";
import { IWithSessionState, hasRight } from "store/features/session";
import { createRequestFetchingSelector } from "store/requests/selectors";
import { getSelectedTeamId } from "store/views/teams";

import { IWithLicensesState, selectDefaultTeamId } from "../license";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { IEntityCollectionState } from "../reducer";

import { TeamRequestActions } from "./actions";
import { ALL_TEAMS_ID } from "./constants";
import { ITeam } from "./interfaces";

export interface IWithTeamsState {
  entities: {
    teams: IEntityCollectionState<ITeam>;
  };
}

export interface ITeamForSelect {
  key: string;
  props: {
    id: string;
    name: string;
  };
}

export function selectTeams(state: IWithTeamsState): { [key: string]: ITeam } {
  return state.entities.teams;
}

export const selectTeamsByIds = createSelector(selectTeams, (teams: { [key: string]: ITeam }) => Object.keys(teams));

export const selectTeamsAlphabetically = createSelector(selectTeams, teams =>
  Object.values(teams).sort(alphabeticalSortBySelector(team => team.name))
);

export function selectTeam(state: IWithTeamsState, id: string): ITeam {
  return state.entities.teams[id] as ITeam;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectTeamsCount = createSelector(selectTeams, (teams: any) => Object.keys(teams).length);

export function selectTeamName(state: IWithTeamsState, id: string): string {
  const team = selectTeam(state, id);
  return team ? team.name : "";
}

function canSeeOtherAgentsTeams(state: IWithSessionState & IWithAgentsState): boolean {
  return hasRight(state, Right.SeeOtherAgentsTeams);
}

export const selectCurrentAgentTeams = createSelector(
  [selectTeamsAlphabetically, canSeeOtherAgentsTeams, selectLoggedInAgent],
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (teams: any, canSeeOthersTeams: any, currentAgent: any) => {
    if (canSeeOthersTeams) {
      return teams;
    }
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return teams.filter((team: any) => currentAgent.teamIds.includes(team.id));
  }
);

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectFirstCurrentAgentTeam = createSelector(selectCurrentAgentTeams, (teams: any) => teams[0]);

export const selectCurrentAgentAssignedTeams = createSelector(
  [selectTeamsAlphabetically, selectLoggedInAgent],
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (teams: any, currentAgent: any) => teams.filter((team: any) => currentAgent.teamIds.includes(team.id))
);

export const selectTeamsPerPermission = createSelector(
  [selectTeamsAlphabetically, selectLoggedInAgent],
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (teams: any, currentAgent: any) => {
    const isAdmin = currentAgent.permission === Role.Owner;
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return teams.filter((team: any) => isAdmin || currentAgent.teamIds.includes(team.id));
  }
);

export const selectTeamsForSelect: (state: IWithTeamsState) => ITeamForSelect[] = createSelector(
  selectTeamsAlphabetically,
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (groups: any) =>
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    groups.map((group: any) => ({
      key: group.id,

      props: {
        id: group.id,
        name: group.name,
      },
    }))
);

export function selectDefaultTeamName(state: IWithLicensesState & IWithTeamsState): string {
  const defaultTeamId = selectDefaultTeamId(state);
  const team = selectTeam(state, defaultTeamId);
  return team.name;
}

export function selectIsTagReminderEnabled(state: IStoreState): boolean {
  const teamId = getSelectedTeamId(state);

  if (teamId === ALL_TEAMS_ID) {
    const teams = selectTeamsAlphabetically(state);
    return teams.every(team => team?.settings?.tagReminder);
  }

  const team = selectTeam(state, teamId);

  if (team) {
    return team.settings?.tagReminder ?? false;
  }

  const defaultTeam = selectTeam(state, selectDefaultTeamId(state));
  return defaultTeam?.settings?.tagReminder ?? false;
}

export const selectIsTeamsFetching = createRequestFetchingSelector([TeamRequestActions.FETCH_COLLECTION]);
